<template>
  <div class="mt-2 mx-2">
    <b-row class="mb-3">
      <b-col>
        <b-form-group label="Predefined Protocols">
          <template v-slot:label="">
            <span style="font-size:15px; font-weight: bold;"
              >Predefined Protocols</span
            >
          </template>
          <template v-slot:description="">
            <div class="mt-2">
              <i class="fas fa-info-circle mr-2 text-primary"></i>
              <i
                >All predefined protocols will take effect starting on the
                selected effective date on the top of this form.</i
              >
            </div>
          </template>
          <multiple-select
            :options="predefinedProtocols ? predefinedProtocols : []"
            v-model="form.predefinedProtocols"
          />
        </b-form-group>
      </b-col>
      <b-col></b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col>
        <b-form-group label="Scheduled Medications">
          <template v-slot:label="">
            <div class="d-flex justify-content-between align-items-end">
              <div style="font-size:15px; font-weight: bold;">
                Scheduled Medications
              </div>
              <div>
                <b-button
                  class=""
                  size="sm"
                  variant="primary"
                  @click="addScheduledMedication"
                  >New Scheduled Medication</b-button
                >
              </div>
            </div>
          </template>
          <grid
            :groupFeature="false"
            :columns="scheduledColumns"
            :autoHeight="true"
            :data="scheduledMedicationItems"
            :columnLines="false"
            :searchFeature="true"
            :stripeFeature="false"
            @renderrows="hookDropdownButtons"
            @renderrow="hookDropdownButtons"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col>
        <b-form-group label="Prn Medications">
          <template v-slot:label="">
            <div class="d-flex justify-content-between align-items-end">
              <div style="font-size:15px; font-weight: bold;">
                Prn Medications
              </div>
              <div>
                <b-button
                  class=""
                  size="sm"
                  variant="primary"
                  @click="addPrnMedication"
                  >New Prn Medication</b-button
                >
              </div>
            </div>
          </template>
          <grid
            :groupFeature="false"
            :columns="prnColumns"
            :autoHeight="true"
            :data="prnMedicationItems"
            :columnLines="false"
            :searchFeature="true"
            :stripeFeature="false"
            @renderrows="hookDropdownButtons"
            @renderrow="hookDropdownButtons"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col>
        <b-form-group label="Discontinue Medications">
          <template v-slot:label="">
            <span style="font-size:15px; font-weight: bold;"
              >Discontinue Medications</span
            >
          </template>
          <grid
            :groupFeature="false"
            :autoHeight="true"
            :columns="discontinueColumns"
            :data="currentMedications"
            :columnLines="false"
            :searchFeature="true"
            :stripeFeature="false"
            @renderrows="hookToggleButtons"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-modal
      centered
      ref="form-scheduled-modal"
      :title="
        (formScheduled.medicationIndex >= 0 ? 'Edit ' : 'Add ') +
          'scheduled medication'
      "
      header-bg-variant="primary"
      header-text-variant="white"
      @ok.prevent="submitScheduledMedication"
      no-close-on-backdrop
      header-class="bg-purple"
      size="md-lg"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-row>
        <b-col>
          <b-form-group
            label="Medication"
            :state="formScheduledErrors.medication.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.medication.join(', ')"
          >
            <v-select
              placeholder="Search for medications"
              v-model="formScheduled.medication"
              @search="searchMedications"
              :options="medications"
              :class="medications.length == 0 ? 'picker-hide-options' : ''"
            >
              <template #open-indicator> <span></span> </template>
            </v-select>
          </b-form-group>
          <b-form-group
            label="Route"
            :state="formScheduledErrors.route.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.route.join(', ')"
          >
            <v-select
              v-model="formScheduled.route"
              :options="medicationRoutes"
            ></v-select>
          </b-form-group>
          <b-row>
            <b-col>
              <b-form-group
                label="Dosage"
                :state="formScheduledErrors.dosage.length == 0 ? null : false"
                :invalid-feedback="formScheduledErrors.dosage.join(', ')"
              >
                <b-form-input
                  v-model="formScheduled.dosage"
                  type="number"
                  trim
                  :state="formScheduledErrors.dosage.length == 0 ? null : false"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Unit of measurement"
                :state="
                  formScheduledErrors.measurementUnit.length == 0 ? null : false
                "
                :invalid-feedback="
                  formScheduledErrors.measurementUnit.join(', ')
                "
              >
                <v-select
                  v-model="formScheduled.measurementUnit"
                  :options="measurements"
                ></v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-form-group
            label="Start Date"
            :state="formScheduledErrors.startDate.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.startDate.join(', ')"
          >
            <date-time-picker
              v-model="formScheduled.startDate"
              :errors="formScheduledErrors.startDate"
              :max="formScheduled.endDate ? formScheduled.endDate : null"
            />
          </b-form-group>
          <b-form-checkbox v-model="formScheduled.untilDischarge">
            Until Discharge
          </b-form-checkbox>
          <b-form-group
            label="End Date"
            :state="formScheduledErrors.endDate.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.endDate.join(', ')"
            :disabled="formScheduled.untilDischarge"
          >
            <date-time-picker
              v-model="formScheduled.endDate"
              :errors="formScheduledErrors.endDate"
              :min="formScheduled.startDate ? formScheduled.startDate : null"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Frequency"
            :state="formScheduledErrors.frequencyId.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.frequencyId.join(', ')"
          >
            <v-select
              v-model="scheduledFrequency"
              :options="frequencyOptions"
            ></v-select>
          </b-form-group>
          <b-form-group
            v-if="formScheduled.frequencyId == 'daysOfWeek'"
            :state="formScheduledErrors.weekDays.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.weekDays.join(', ')"
          >
            <week-days-picker v-model="formScheduled.weekDays" />
          </b-form-group>
          <b-form-group
            v-if="formScheduled.frequencyId == 'daysInterval'"
            label="Days interval"
            :state="
              formScheduledErrors.daysIntervalId.length == 0 ? null : false
            "
            :invalid-feedback="formScheduledErrors.daysIntervalId.join(', ')"
          >
            <v-select
              v-model="daysInterval"
              :options="daysIntervalOptions"
            ></v-select>
          </b-form-group>
          <b-form-group
            label="Times per day"
            :state="
              formScheduledErrors.timesPerDayId.length == 0 ? null : false
            "
            :invalid-feedback="formScheduledErrors.timesPerDayId.join(', ')"
          >
            <v-select
              v-model="timesPerDay"
              :options="timesPerDayOptions"
            ></v-select>
          </b-form-group>
          <b-form-group
            label="Times"
            :state="formScheduledErrors.medication.length == 0 ? null : false"
            :invalid-feedback="formScheduledErrors.medication.join(', ')"
          >
            <div
              class="times-container rounded border w-100 d-flex flex-wrap justify-content-start align-items-start p-2"
            >
              <div
                v-for="timeIndex in Object.keys(formScheduled.times)"
                :key="timeIndex"
                class="mr-2"
              >
                <b-form-select
                  :options="timeOptions"
                  :value="formScheduled.times[timeIndex]"
                  @input="newValue => setTime(timeIndex, newValue)"
                ></b-form-select>
              </div>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Instructions">
            <b-form-input
              v-model="formScheduled.instructions"
              trim
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
    <b-modal
      centered
      ref="form-prn-modal"
      :title="
        (formPrn.medicationIndex >= 0 ? 'Edit ' : 'Add ') + 'prn medication'
      "
      header-bg-variant="primary"
      header-text-variant="white"
      @ok.prevent="submitPrnMedication"
      no-close-on-backdrop
      header-class="bg-purple"
      size="md-lg"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-row>
        <b-col>
          <b-form-group
            label="Medication"
            :state="formPrnErrors.medication.length == 0 ? null : false"
            :invalid-feedback="formPrnErrors.medication.join(', ')"
          >
            <v-select
              placeholder="Search for medications"
              v-model="formPrn.medication"
              @search="searchMedications"
              :options="medications"
              :class="medications.length == 0 ? 'picker-hide-options' : ''"
            >
              <template #open-indicator> <span></span> </template>
            </v-select>
          </b-form-group>
          <b-form-group
            label="Route"
            :state="formPrnErrors.route.length == 0 ? null : false"
            :invalid-feedback="formPrnErrors.route.join(', ')"
          >
            <v-select
              v-model="formPrn.route"
              :options="medicationRoutes"
            ></v-select>
          </b-form-group>
          <b-row>
            <b-col>
              <b-form-group
                label="Dosage"
                :state="formPrnErrors.dosage.length == 0 ? null : false"
                :invalid-feedback="formPrnErrors.dosage.join(', ')"
              >
                <b-form-input
                  v-model="formPrn.dosage"
                  type="number"
                  trim
                  :state="formPrnErrors.dosage.length == 0 ? null : false"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Unit of measurement"
                :state="
                  formPrnErrors.measurementUnit.length == 0 ? null : false
                "
                :invalid-feedback="formPrnErrors.measurementUnit.join(', ')"
              >
                <v-select
                  v-model="formPrn.measurementUnit"
                  :options="measurements"
                ></v-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col>
          <b-form-group
            label="Start Date"
            :state="formPrnErrors.startDate.length == 0 ? null : false"
            :invalid-feedback="formPrnErrors.startDate.join(', ')"
          >
            <date-time-picker
              v-model="formPrn.startDate"
              :errors="formPrnErrors.startDate"
              :max="formPrn.endDate ? formPrn.endDate : null"
            />
          </b-form-group>
          <b-form-group
            label="End Date"
            :state="formPrnErrors.endDate.length == 0 ? null : false"
            :invalid-feedback="formPrnErrors.endDate.join(', ')"
          >
            <date-time-picker
              v-model="formPrn.endDate"
              :errors="formPrnErrors.endDate"
              :min="formPrn.startDate ? formPrn.startDate : null"
            />
          </b-form-group>
          <b-form-group label="Maximum Dosage Per Day">
            <b-form-input
              v-model="formPrn.maximumDosagePerDay"
              trim
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Instructions">
            <b-form-input v-model="formPrn.instructions" trim></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../../third_party/grid/Grid.vue";
import doctorOrderServer from "../../../server/doctorOrder";
import { mapActions, mapState } from "vuex";
import {
  validateNumberLessEqualThan,
  validateRequired
} from "../../../helpers/validation";
import medicationServer from "../../../server/medication";
import scheduledMedicationServer from "../../../server/scheduledMedication";
import prnMedicationServer from "../../../server/prnMedication";
import {
  frequencies,
  daysIntervals,
  timesPerDayOptions,
  timeOptions
} from "../../../config/medications";
import WeekDaysPicker from "../../controls/WeekDaysPicker";
import DateTimePicker from "../../controls/DateTimePicker.vue";
import MultipleSelect from "../../controls/MultipleSelect";

export default {
  name: "DocumentDoctorOrder",
  components: {
    Grid,
    WeekDaysPicker,
    DateTimePicker,
    MultipleSelect
  },
  props: ["documentId", "documentStatus", "effectiveDateTime", "eocId"],
  data() {
    return {
      medications: [],
      formId: null,
      form: {
        scheduledMedications: [],
        prnMedications: [],
        predefinedProtocols: [],
        discontinuedMedications: []
      },
      formErrors: {
        scheduledMedications: [],
        prnMedications: [],
        predefinedProtocols: []
      },
      formScheduled: {
        medication: null,
        route: null,
        dosage: "",
        measurementUnit: null,
        startDate: null,
        endDate: null,
        untilDischarge: false,
        frequencyId: null,
        daysIntervalId: null,
        weekDays: [],
        timesPerDayId: null,
        times: [],
        instructions: ""
      },
      formScheduledErrors: {
        medication: [],
        route: [],
        dosage: [],
        measurementUnit: [],
        startDate: [],
        endDate: [],
        frequencyId: [],
        daysIntervalId: [],
        weekDays: [],
        timesPerDayId: []
      },
      formPrn: {
        medication: null,
        route: null,
        dosage: "",
        measurementUnit: null,
        startDate: null,
        endDate: null,
        instructions: "",
        maximumDosagePerDay: ""
      },
      formPrnErrors: {
        medication: [],
        route: [],
        dosage: [],
        measurementUnit: [],
        startDate: [],
        endDate: []
      },
      scheduledMedications: [],
      prnMedications: [],
      currentMedications: []
    };
  },
  async mounted() {
    this.fetchMedicationRoutes();
    this.fetchMeasurements();
    const predefined = this.fetchPredefinedProtocols();
    const scheduled = this.fetchScheduledMedications();
    const prn = this.fetchPrnMedications();

    if (this.documentId) {
      await this.fecthDoctorOrder();
      this.calculateMedicationsInsideRange();
    }

    await Promise.all([predefined, scheduled, prn]);
    this.mounted = true;
    this.$emit("mounted", this.getBakedLayout());
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", [
      "fetchPredefinedProtocols",
      "fetchMedicationRoutes",
      "fetchMeasurements"
    ]),
    async fecthDoctorOrder() {
      const doctorOrder = await doctorOrderServer.getFromDocument(
        this.documentId
      );
      if (doctorOrder) {
        this.formId = doctorOrder.id;
        this.form = {
          scheduledMedications: doctorOrder.scheduledMedications.map(
            medication => {
              const times = {};
              for (let index = 0; index < medication.times.length; index++) {
                const time = medication.times[index];
                times[index] = time;
              }
              return {
                medication: medication.medication
                  ? {
                      value: medication.medication.id,
                      label: medication.medication.name
                    }
                  : null,
                route: medication.medicationRoute
                  ? {
                      value: medication.medicationRoute.id,
                      label: medication.medicationRoute.name
                    }
                  : null,
                dosage: medication.dosageAmount,
                measurementUnit: medication.unitOfMeasurement
                  ? {
                      value: medication.unitOfMeasurement.id,
                      label: medication.unitOfMeasurement.name
                    }
                  : null,
                startDate: medication.startDate
                  ? this.formatDateTimeMysql(medication.startDate)
                  : null,
                endDate: medication.endDate
                  ? this.formatDateTimeMysql(medication.endDate)
                  : null,
                untilDischarge: medication.untilDischarge,
                frequencyId:
                  medication.frequency.charAt(0).toLowerCase() +
                  medication.frequency.slice(1),
                daysIntervalId: this.getDaysIntervalId(
                  medication.interval,
                  medication.intervalAmount
                ),
                weekDays: [
                  ...(medication.sunday ? ["0"] : []),
                  ...(medication.monday ? ["1"] : []),
                  ...(medication.tuesday ? ["2"] : []),
                  ...(medication.wednesday ? ["3"] : []),
                  ...(medication.thursday ? ["4"] : []),
                  ...(medication.friday ? ["5"] : []),
                  ...(medication.saturday ? ["6"] : [])
                ],
                timesPerDayId: `${medication.timesPerDay}Time${
                  medication.timesPerDay > 1 ? "s" : ""
                }PerDay`,
                times: times,
                instructions: medication.instructions,
                predefinedProtocol: medication.predefinedProtocol
                  ? {
                      value: medication.predefinedProtocol.id,
                      label: medication.predefinedProtocol.name
                    }
                  : null
              };
            }
          ),
          prnMedications: doctorOrder.prnMedications.map(prnMedication => {
            return {
              medication: prnMedication.medication
                ? {
                    value: prnMedication.medication.id,
                    label: prnMedication.medication.name
                  }
                : null,
              route: prnMedication.medicationRoute
                ? {
                    value: prnMedication.medicationRoute.id,
                    label: prnMedication.medicationRoute.name
                  }
                : null,
              dosage: prnMedication.dosageAmount,
              measurementUnit: prnMedication.unitOfMeasurement
                ? {
                    value: prnMedication.unitOfMeasurement.id,
                    label: prnMedication.unitOfMeasurement.name
                  }
                : null,
              startDate: prnMedication.startDate
                ? this.formatDateTimeMysql(prnMedication.startDate)
                : null,
              endDate: prnMedication.stopDate
                ? this.formatDateTimeMysql(prnMedication.stopDate)
                : null,
              instructions: prnMedication.instructions,
              maximumDosagePerDay: prnMedication.maximumDosagePerDay,
              predefinedProtocol: prnMedication.predefinedProtocol
                ? {
                    value: prnMedication.predefinedProtocol.id,
                    label: prnMedication.predefinedProtocol.name
                  }
                : null
            };
          }),
          predefinedProtocols: doctorOrder.predefinedProtocols.map(pP => ({
            value: pP.id,
            label: pP.name,
            data: pP
          })),
          discontinuedMedications: doctorOrder.discontinuedMedications.map(
            m => ({
              ...(m.scheduledMedication
                ? { id: m.scheduledMedication.id, type: "scheduled" }
                : { id: m.prnMedication.id, type: "prn" })
            })
          )
        };
      }
    },
    async fetchScheduledMedications() {
      this.scheduledMedications = await scheduledMedicationServer.query(
        this.eocId,
        {}
      );
    },
    async fetchPrnMedications() {
      const prnMedications = await prnMedicationServer.query(this.eocId, {});
      this.prnMedications = prnMedications.map(m => ({
        ...m,
        endDate: m.stopDate
      }));
    },
    validateDoctorOrderSubmit() {
      let withoutErrors = true;

      return withoutErrors;
    },
    async submitComponentData(documentId) {
      const result = this.validateDoctorOrderSubmit();
      if (result) {
        const formData = {
          documentId: documentId,
          scheduledMedications: this.form.scheduledMedications.map(
            scheduledMedication => {
              return {
                medicationId: scheduledMedication.medication.value,
                dosageAmount: parseFloat(scheduledMedication.dosage),
                unitOfMeasurementId: scheduledMedication.measurementUnit.value,
                medicationRouteId: scheduledMedication.route.value,
                instructions: scheduledMedication.instructions,
                startDate: scheduledMedication.startDate
                  ? this.formatDateTimeSql(scheduledMedication.startDate)
                  : null,
                endDate: scheduledMedication.endDate
                  ? this.formatDateTimeSql(scheduledMedication.endDate)
                  : null,
                untilDischarge: scheduledMedication.untilDischarge,
                frequency:
                  scheduledMedication.frequencyId.charAt(0).toUpperCase() +
                  scheduledMedication.frequencyId.slice(1),
                interval: this.toIntervalAmounts(scheduledMedication).frequency,
                intervalAmount: this.toIntervalAmounts(scheduledMedication)
                  .amount,
                timesPerDay: Object.values(scheduledMedication.times).length,
                monday: scheduledMedication.weekDays.indexOf("1") >= 0,
                tuesday: scheduledMedication.weekDays.indexOf("2") >= 0,
                wednesday: scheduledMedication.weekDays.indexOf("3") >= 0,
                thursday: scheduledMedication.weekDays.indexOf("4") >= 0,
                friday: scheduledMedication.weekDays.indexOf("5") >= 0,
                saturday: scheduledMedication.weekDays.indexOf("6") >= 0,
                sunday: scheduledMedication.weekDays.indexOf("0") >= 0,
                times: Object.values(scheduledMedication.times),
                predefinedProtocolId:
                  scheduledMedication.predefinedProtocol?.value
              };
            }
          ),
          prnMedications: this.form.prnMedications.map(prnMedication => {
            return {
              medicationId: prnMedication.medication.value,
              dosageAmount: parseFloat(prnMedication.dosage),
              unitOfMeasurementId: prnMedication.measurementUnit.value,
              medicationRouteId: prnMedication.route.value,
              instructions: prnMedication.instructions,
              maximumDosagePerDay: prnMedication.maximumDosagePerDay,
              startDate: prnMedication.startDate
                ? this.formatDateTimeSql(prnMedication.startDate)
                : null,
              stopDate: prnMedication.endDate
                ? this.formatDateTimeSql(prnMedication.endDate)
                : null,
              predefinedProtocolId: prnMedication.predefinedProtocol?.value
            };
          }),
          predefinedProtocolIds: this.form.predefinedProtocols.map(
            pP => pP.value
          ),
          discontinuedMedications: this.form.discontinuedMedications.map(m => ({
            ...(m.type == "scheduled"
              ? { scheduledMedicationId: m.id }
              : { prnMedicationId: m.id })
          }))
        };
        if (this.formId) {
          await doctorOrderServer.update(this.formId, formData);
        } else {
          await doctorOrderServer.create(formData);
        }
      }
    },
    async searchMedications(search, loading) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      if (search && search.trim().length >= 3) {
        this.medications = [];
      }

      this.lastTimeout = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const medications = await medicationServer.query(
            {
              searchPhrase: search,
              page: 0,
              pageSize: 1000000,
              orderBy: "name"
            },
            { "Hide-Loading": true }
          );
          this.medications = medications.map(m => ({
            value: m.id,
            label: m.name
          }));
        } else {
          this.medications = [];
        }
        loading(false);
      }, 1000);
    },
    clearErrors() {
      this.formScheduledErrors = {
        medication: [],
        route: [],
        dosage: [],
        measurementUnit: [],
        startDate: [],
        endDate: [],
        frequencyId: [],
        daysIntervalId: [],
        weekDays: [],
        timesPerDayId: []
      };
      this.formPrnErrors = {
        medication: [],
        route: [],
        dosage: [],
        measurementUnit: [],
        startDate: [],
        endDate: []
      };
    },
    addScheduledMedication() {
      this.formScheduled = {
        medication: null,
        route: null,
        dosage: "",
        measurementUnit: null,
        startDate: null,
        untilDischarge: false,
        endDate: null,
        frequencyId: null,
        daysIntervalId: null,
        weekDays: [],
        timesPerDayId: null,
        times: [],
        instructions: ""
      };
      this.clearErrors();
      this.$refs["form-scheduled-modal"].show();
    },
    addPrnMedication() {
      this.formPrn = {
        medication: null,
        route: null,
        dosage: "",
        measurementUnit: null,
        startDate: null,
        endDate: null,
        instructions: "",
        maximumDosagePerDay: ""
      };
      this.clearErrors();
      this.$refs["form-prn-modal"].show();
    },
    validateScheduledSubmit() {
      let withoutErrors = true;

      this.formScheduledErrors.medication = [];
      if (!validateRequired(this.formScheduled.medication)) {
        this.formScheduledErrors.medication.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.route = [];
      if (!validateRequired(this.formScheduled.route)) {
        this.formScheduledErrors.route.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.dosage = [];
      if (!validateRequired(this.formScheduled.dosage)) {
        this.formScheduledErrors.dosage.push("This field is required");
        withoutErrors = false;
      }
      if (
        this.formScheduled.dosage &&
        !validateNumberLessEqualThan(this.formScheduled.dosage, 100000000)
      ) {
        this.formScheduledErrors.dosage.push("Maximum value of 100000000");
        withoutErrors = false;
      }

      this.formScheduledErrors.measurementUnit = [];
      if (!validateRequired(this.formScheduled.measurementUnit)) {
        this.formScheduledErrors.measurementUnit.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.startDate = [];
      if (!validateRequired(this.formScheduled.startDate)) {
        this.formScheduledErrors.startDate.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.endDate = [];
      if (
        !this.formScheduled.untilDischarge &&
        !validateRequired(this.formScheduled.endDate)
      ) {
        this.formScheduledErrors.endDate.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.frequencyId = [];
      if (!validateRequired(this.formScheduled.frequencyId)) {
        this.formScheduledErrors.frequencyId.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.weekDays = [];
      if (
        this.formScheduled.frequencyId == "daysOfWeek" &&
        this.formScheduled.weekDays.length == 0
      ) {
        this.formScheduledErrors.weekDays.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.daysIntervalId = [];
      if (
        this.formScheduled.frequencyId == "daysInterval" &&
        !validateRequired(this.formScheduled.daysIntervalId)
      ) {
        this.formScheduledErrors.daysIntervalId.push("This field is required");
        withoutErrors = false;
      }

      this.formScheduledErrors.timesPerDayId = [];
      if (!validateRequired(this.formScheduled.timesPerDayId)) {
        this.formScheduledErrors.timesPerDayId.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitScheduledMedication() {
      const result = await this.validateScheduledSubmit();
      if (result) {
        if (this.formScheduled.medicationIndex >= 0) {
          this.form.scheduledMedications.splice(
            this.formScheduled.medicationIndex,
            1,
            {
              ...this.formScheduled
            }
          );
        } else {
          this.form.scheduledMedications.push({
            ...this.formScheduled
          });
        }

        this.$refs["form-scheduled-modal"].hide();
      }
    },
    validatePrnSubmit() {
      let withoutErrors = true;

      this.formPrnErrors.medication = [];
      if (!validateRequired(this.formPrn.medication)) {
        this.formPrnErrors.medication.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.route = [];
      if (!validateRequired(this.formPrn.route)) {
        this.formPrnErrors.route.push("This field is required");
        withoutErrors = false;
      }

      this.formPrnErrors.dosage = [];
      if (!validateRequired(this.formPrn.dosage)) {
        this.formPrnErrors.dosage.push("This field is required");
        withoutErrors = false;
      }
      if (
        this.formPrn.dosage &&
        !validateNumberLessEqualThan(this.formPrn.dosage, 100000000)
      ) {
        this.formPrnErrors.dosage.push("Maximum value of 100000000");
        withoutErrors = false;
      }

      this.formPrnErrors.measurementUnit = [];
      if (!validateRequired(this.formPrn.measurementUnit)) {
        this.formPrnErrors.measurementUnit.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitPrnMedication() {
      const result = await this.validatePrnSubmit();
      if (result) {
        if (this.formPrn.medicationIndex >= 0) {
          this.form.prnMedications.splice(this.formPrn.medicationIndex, 1, {
            ...this.formPrn
          });
        } else {
          this.form.prnMedications.push({
            ...this.formPrn
          });
        }

        this.$refs["form-prn-modal"].hide();
      }
    },
    initializeTimes() {
      if (!this.formScheduled.timesPerDayId) {
        this.formScheduled.times = [];
      } else {
        let newTimes = {};
        for (
          let index = 0;
          index <
          timesPerDayOptions[this.formScheduled.timesPerDayId].initialTimes
            .length;
          index++
        ) {
          const initialTime =
            timesPerDayOptions[this.formScheduled.timesPerDayId].initialTimes[
              index
            ];
          newTimes[index] = initialTime;
        }
        this.formScheduled.times = newTimes;
      }
    },
    setTime(timeIndex, newTime) {
      this.formScheduled.times[timeIndex] = newTime;
    },
    getDaysIntervalId(intervalLabel, intervalAmount) {
      switch (`${intervalAmount}${intervalLabel}`) {
        case "2Days":
          return "every2Days";
        case "3Days":
          return "every3Days";
        case "1Weeks":
          return "1timePerWeek";
        case "2Weeks":
          return "everyTwoWeeks";
        case "3Weeks":
          return "every3Weeks";
        case "28Days":
          return "every28Days";
        case "30Days":
          return "every30Days";

        default:
          return null;
      }
    },
    toIntervalAmounts(scheduledMedication) {
      switch (scheduledMedication.daysIntervalId) {
        case "every2Days":
          return {
            frequency: "Days",
            amount: 2
          };
        case "every3Days":
          return {
            frequency: "Days",
            amount: 3
          };
        case "1timePerWeek":
          return {
            frequency: "Weeks",
            amount: 1
          };
        case "everyTwoWeeks":
          return {
            frequency: "Weeks",
            amount: 2
          };
        case "every3Weeks":
          return {
            frequency: "Weeks",
            amount: 3
          };
        case "every28Days":
          return {
            frequency: "Days",
            amount: 28
          };
        case "every30Days":
          return {
            frequency: "Days",
            amount: 30
          };

        default:
          return {
            frequency: "Days",
            amount: 1
          };
      }
    },
    getBakedLayout() {
      return `<div class="mt-2 mx-2" style="font-size:13px;">
        <div class="row mb-3">
            <div class="col">
              <fieldset class="form-group" >
                  <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0" style="font-size: 15px; font-weight: bold;">Predefined Protocols</legend>
                  <div>
                  ${
                    this.form.predefinedProtocols.length > 0
                      ? `
                    <div class="multiple-select border rounded border-color-light-gray">
                      <div class="select-list-container">
                        <div class="list-group mt-1 p-1 forms-list">
                            <div >
                            ${this.form.predefinedProtocols
                              .map(
                                item => `
                              <div class="list-group-item filter-value border pl-2 pr-2 pt-1 pb-1 mr-2 ml-1 mt-2">
                                  <div> ${item.label} </div>
                              </div>
                              `
                              )
                              .join("")}
                            </div>
                        </div>
                      </div>
                    </div>
                    `
                      : ""
                  }
                  </div>
              </fieldset>
            </div>
            <div class="col"></div>
        </div>
        <div class="row mb-3">
            <div class="col">
              <fieldset class="form-group" >
                  <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0" style="font-size: 15px; font-weight: bold;">
                    <div class="d-flex justify-content-between">
                        <div>Scheduled Medications</div>
                    </div>
                  </legend>
                  <div tabindex="-1" role="group" class="bv-no-focus-ring">
                    <table role="table" aria-busy="false" aria-colcount="3" class="table b-table form-builder-table table-striped table-hover table-bordered">
                      <thead role="rowgroup" class="">
                        <tr role="row" class="">
                          <th role="columnheader" scope="col" aria-colindex="1" class="">
                            <div style="word-break: keep-all">Date Range</div>
                          </th>
                          <th role="columnheader" scope="col" aria-colindex="2" class="">
                            <div style="word-break: keep-all">Medication</div>
                          </th>
                          <th role="columnheader" scope="col" aria-colindex="3" class="">
                            <div style="word-break: keep-all">Times</div>
                          </th>
                          <th role="columnheader" scope="col" aria-colindex="4" class="" style="width:100px">
                            <div style="word-break: keep-all">Frequency</div>
                          </th>
                          <th role="columnheader" scope="col" aria-colindex="5" class="">
                            <div style="word-break: keep-all">Instructions</div>
                          </th>
                        </tr>
                      </thead>
                      <tbody role="rowgroup">
                        ${this.form.scheduledMedications
                          .map(
                            item => `
                        <tr role="row" class="">
                          <td aria-colindex="1" role="cell" class="" style="word-break: keep-all">${`${this.formatDate(
                            item.startDate
                          )} - ${
                            item.untilDischarge
                              ? "Until Discharge"
                              : this.formatDate(item.endDate)
                          }`}</td>   
                          <td aria-colindex="2" role="cell" class="" style="word-break: keep-all">${`<div class="text-wrap">${item.medication.label}, ${item.dosage} ${item.measurementUnit.label} by ${item.route.label}</div>`}</td>   
                          <td aria-colindex="3" role="cell" class="" style="word-break: keep-all">${Object.values(
                            item.times
                          )
                            .map(time => this.formatTimeOnly(time, "HH:mm"))
                            .join(", ")}</td>   
                          <td aria-colindex="4" role="cell" class="" style="word-break: keep-all">${this.getTableTimesLabel(
                            item
                          )}</td>   
                          <td aria-colindex="5" role="cell" class="" style="word-break: keep-all">${
                            item.instructions ? item.instructions : ""
                          }</td>                          
                        </tr>
                        `
                          )
                          .join("")}
                      </tbody>
                    </table>
                  </div>
              </fieldset>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col">
              <fieldset class="form-group" >
                  <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0" style="font-size: 15px; font-weight: bold;">
                    <div class="d-flex justify-content-between">
                        <div>Prn Medications</div>
                    </div>
                  </legend>
                  <div tabindex="-1" role="group" class="bv-no-focus-ring">
                    <table role="table" aria-busy="false" aria-colcount="3" class="table b-table form-builder-table table-striped table-hover table-bordered">
                      <thead role="rowgroup" class="">
                        <tr role="row" class="">
                        <th role="columnheader" scope="col" aria-colindex="1" class="">
                            <div style="word-break: keep-all">Date Range</div>
                          </th>
                          <th role="columnheader" scope="col" aria-colindex="2" class="">
                            <div style="word-break: keep-all">Medication</div>
                          </th>
                          <th role="columnheader" scope="col" aria-colindex="5" class="">
                            <div style="word-break: keep-all">Instructions</div>
                          </th>
                          <th role="columnheader" scope="col" aria-colindex="6" class="" style="width:100px">
                            <div style="word-break: keep-all">Maximum Dosage</div>
                          </th>
                        </tr>
                      </thead>
                      <tbody role="rowgroup">
                        ${this.form.prnMedications
                          .map(
                            item => `
                        <tr role="row" class="">
                          <td aria-colindex="1" role="cell" class="" style="word-break: keep-all">${`${this.formatDate(
                            item.startDate
                          )} - ${
                            item.untilDischarge
                              ? "Until Discharge"
                              : this.formatDate(item.endDate)
                          }`}</td>   
                          <td aria-colindex="2" role="cell" class="" style="word-break: keep-all">${`<div class="text-wrap">${item.medication.label}, ${item.dosage} ${item.measurementUnit.label} by ${item.route.label}</div>`}</td>   
                          <td aria-colindex="5" role="cell" class="" style="word-break: keep-all">${
                            item.instructions ? item.instructions : ""
                          }</td>  
                          <td aria-colindex="5" role="cell" class="" style="word-break: keep-all">${
                            item.maximumDosagePerDay
                              ? item.maximumDosagePerDay
                              : ""
                          }</td>                          
                        </tr>
                        `
                          )
                          .join("")}
                      </tbody>
                    </table>
                  </div>
              </fieldset>
            </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            <fieldset class="form-group">
              <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0" style="font-size: 15px; font-weight: bold;">
                <div class="d-flex justify-content-between">
                  <div>Discontinued Medications</div>
                </div>
              </legend>
              <div tabindex="-1" role="group" class="bv-no-focus-ring">
                <table role="table" aria-busy="false" aria-colcount="3" class="table b-table form-builder-table table-striped table-hover table-bordered">
                  <thead role="rowgroup" class="">
                    <tr role="row" class="">
                      <th role="columnheader" scope="col" aria-colindex="1" class="">
                        <div style="word-break: keep-all">Date Range</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex="2" class="">
                        <div style="word-break: keep-all">Medication</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex="3" class="">
                        <div style="word-break: keep-all">Times</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex="4" class="" style="width:100px">
                        <div style="word-break: keep-all">Frequency</div>
                      </th>
                      <th role="columnheader" scope="col" aria-colindex="5" class="">
                        <div style="word-break: keep-all">Instructions</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody role="rowgroup">
                    ${this.currentMedications
                      .filter(cM =>
                        this.form.discontinuedMedications.some(
                          u => u.id == cM.id
                        )
                      )
                      .map(
                        item => `
                    <tr role="row">
                      <td aria-colindex="1" role="cell" style="word-break: keep-all">
                        ${
                          item.startDate
                            ? this.formatDate(item.startDate)
                            : "Not selected"
                        }
                        - 
                        ${
                          item.untilDischarge
                            ? "Until Discharge"
                            : item.endDate
                            ? this.formatDate(item.endDate)
                            : "Not selected"
                        }
                      </td>
                      <td aria-colindex="2" role="cell" style="word-break: keep-all">${`<div class="text-wrap">${item.medication.label}, ${item.dosage} ${item.measurementUnit.label} by ${item.route.label}</div>`}</td>
                      <td aria-colindex="3" role="cell" style="word-break: keep-all">${
                        item.times
                          ? Object.values(item.times)
                              .map(time => this.formatTimeOnly(time, "HH:mm"))
                              .join(", ")
                          : ""
                      }</td> 
                      <td aria-colindex="4" role="cell" style="word-break: keep-all">
                      ${
                        item.frequencyId ? this.getTableTimesLabel(item) : ""
                      }</td>
                      <td aria-colindex="5" role="cell" style="word-break: keep-all">${
                        item.instructions ? item.instructions : ""
                      }</td>
                    </tr>
                      `
                      )
                      .join("")}
                  </tbody>
                </table>
              </div>
            </fieldset>
            
          </div>
        </div>
      </div>`;
    },
    getTableTimesLabel(item) {
      switch (item.frequencyId) {
        case "everyDay":
          return "Every Day";
        case "daysInterval":
          return daysIntervals[item.daysIntervalId].label;
        case "daysOfWeek":
          return [
            ...(item.weekDays.includes("0") ? ["Sunday"] : []),
            ...(item.weekDays.includes("1") ? ["Monday"] : []),
            ...(item.weekDays.includes("2") ? ["Tuesday"] : []),
            ...(item.weekDays.includes("3") ? ["Wednesday"] : []),
            ...(item.weekDays.includes("4") ? ["Thursday"] : []),
            ...(item.weekDays.includes("5") ? ["Friday"] : []),
            ...(item.weekDays.includes("6") ? ["Saturday"] : [])
          ].join(", ");
        default:
          return "";
      }
    },
    mapProtocolScheduledMedication(m) {
      const datetime = new Date(this.effectiveDateTime);

      let frequencyId = "everyDay";
      let daysIntervalId = null;
      let weekDays = [];
      if (m.frequency == "DaysInterval") {
        frequencyId = "daysInterval";
        daysIntervalId =
          m.interval == "Weeks" && m.intervalAmount == "1"
            ? "1timePerWeek"
            : m.interval == "Weeks" && m.intervalAmount == "2"
            ? "everyTwoWeeks"
            : `every${m.intervalAmount}${m.interval}`;
      } else if (m.frequency == "DaysOfWeek") {
        frequencyId = "daysOfWeek";
        weekDays = [
          ...(m.sunday ? ["0"] : []),
          ...(m.monday ? ["1"] : []),
          ...(m.tuesday ? ["2"] : []),
          ...(m.wednesday ? ["3"] : []),
          ...(m.thursday ? ["4"] : []),
          ...(m.friday ? ["5"] : []),
          ...(m.saturday ? ["6"] : [])
        ];
      }
      return {
        id: m.id,
        type: "scheduled",
        startDate: m.startDate
          ? this.formatDateTimeMysql(m.startDate)
          : this.formatDateTimeMysql(
              new Date(this.effectiveDateTime).setDate(
                datetime.getDate() + m.fromDay - 1
              )
            ),
        endDate: m.endDate
          ? this.formatDateTimeMysql(m.endDate)
          : m.toDay
          ? this.formatDateTimeMysql(
              new Date(this.effectiveDateTime).setDate(
                datetime.getDate() + m.toDay - 1
              )
            )
          : null,
        dosage: `${m.dosageAmount}`,
        instructions: m.instructions,
        times: m.times,
        medication: {
          value: m.medication.id,
          label: m.medication.name
        },
        measurementUnit: {
          value: m.unitOfMeasurement.id,
          label: m.unitOfMeasurement.name
        },
        route: {
          value: m.medicationRoute.id,
          label: m.medicationRoute.name
        },
        frequencyId,
        daysIntervalId,
        weekDays,
        timesPerDayId: m.times.length
          ? this.timesPerDayOptions[m.times.length - 1].value
          : null,
        untilDischarge: m.untilDischarge,
        predefinedProtocol: this.predefinedProtocols.find(
          u => u.value == m.predefinedProtocolId
        )
      };
    },
    mapProtocolPrnMedication(m) {
      const datetime = new Date(this.effectiveDateTime);

      return {
        id: m.id,
        type: "prn",
        startDate: m.startDate
          ? this.formatDateTimeMysql(m.startDate)
          : m.fromDay
          ? this.formatDateTimeMysql(
              datetime.setDate(datetime.getDate() + m.fromDay - 1)
            )
          : null,
        endDate: m.endDate
          ? this.formatDateTimeMysql(m.endDate)
          : m.toDay
          ? this.formatDateTimeMysql(
              datetime.setDate(datetime.getDate() + m.toDay - 1)
            )
          : null,
        dosage: `${m.dosageAmount}`,
        instructions: m.instructions,
        maximumDosagePerDay: m.maximumDosagePerDay,
        medication: {
          value: m.medication.id,
          label: m.medication.name
        },
        measurementUnit: {
          value: m.unitOfMeasurement.id,
          label: m.unitOfMeasurement.name
        },
        route: {
          value: m.medicationRoute.id,
          label: m.medicationRoute.name
        },
        predefinedProtocol: this.predefinedProtocols.find(
          u => u.value == m.predefinedProtocolId
        )
      };
    },
    calculateMedicationsInsideRange() {
      var self = this;
      const insideRange = m => {
        const startDate = m.startDate && new Date(m.startDate);
        const endDate = m.endDate && new Date(m.endDate);
        const effectiveDate = new Date(this.effectiveDateTime);
        if (self.documentStatus == "Completed") {
          return self.form.discontinuedMedications.some(dM => dM.id == m.id);
        }
        const isDiscontinued = this.form.discontinuedMedications.some(
          u => u.id == m.id
        );
        return (
          isDiscontinued ||
          ((!startDate || startDate < effectiveDate) &&
            (!endDate || m.untilDischarge || endDate > effectiveDate))
        );
      };

      const scheduled = this.scheduledMedications
        .filter(insideRange)
        .map(this.mapProtocolScheduledMedication);

      const prn = this.prnMedications
        .filter(insideRange)
        .map(this.mapProtocolPrnMedication);

      this.currentMedications = scheduled.concat(prn);
    },
    recalculatePredefinedProtocols() {
      let formScheduledMedications = [];
      for (
        let index = 0;
        index < this.form.scheduledMedications.length;
        index++
      ) {
        const scheduledMedication = this.form.scheduledMedications[index];
        if (!scheduledMedication.predefinedProtocol) {
          formScheduledMedications.push(scheduledMedication);
        }
      }

      let formPrnMedications = [];
      for (let index = 0; index < this.form.prnMedications.length; index++) {
        const prnMedication = this.form.prnMedications[index];
        if (!prnMedication.predefinedProtocol) {
          formPrnMedications.push(prnMedication);
        }
      }

      for (
        let index = 0;
        index < this.form.predefinedProtocols.length;
        index++
      ) {
        const predefinedProtocol = this.predefinedProtocols.find(
          pP => pP.value == this.form.predefinedProtocols[index].value
        );
        formScheduledMedications = formScheduledMedications.concat(
          predefinedProtocol.data.medications.map(
            this.mapProtocolScheduledMedication
          )
        );
        formPrnMedications = formPrnMedications.concat(
          predefinedProtocol.data.prnMedications.map(
            this.mapProtocolPrnMedication
          )
        );
      }

      this.form.scheduledMedications = formScheduledMedications;
      this.form.prnMedications = formPrnMedications;
    }
  },
  computed: {
    ...mapState("client", {
      predefinedProtocols: state =>
        state.predefinedProtocols.map(pP => ({
          value: pP.id,
          label: pP.name,
          data: pP
        })),
      medicationRoutes: state =>
        state.medicationRoutes.map(medicationRoute => ({
          value: medicationRoute.id,
          label: medicationRoute.name
        })),
      measurements: state =>
        state.measurements.map(measurement => ({
          value: measurement.id,
          label: measurement.name
        }))
    }),
    scheduledMedicationItems() {
      return this.form.scheduledMedications.map((item, itemIndex) => {
        let rowHeight = 50;
        const singleLineHeight = 25;
        const charsMedicationPerLine = 40;
        const charsInstructionsPerLine = 50;
        const maxFactor = Math.max(
          Math.ceil(
            `${item.medication.label}, ${item.dosageAmount} ${item.measurementUnit.label} by ${item.route.label}`
              .length / charsMedicationPerLine
          ),
          Math.ceil(item.instructions.length / charsInstructionsPerLine)
        );
        if (maxFactor > 1) rowHeight += singleLineHeight * (maxFactor - 1);
        return {
          medicationIndex: itemIndex,
          ...item,
          rowHeight: rowHeight,
          inputRows: maxFactor
        };
      });
    },
    scheduledColumns() {
      return [
        {
          text: "Date Range",
          flex: 1,
          type: "template",
          editor: false,
          sortable: false,
          template: data =>
            `<div class="text-wrap">
              ${this.formatDate(data.record.data.startDate)} - ${
              data.record.data.untilDischarge
                ? "Until Discharge"
                : this.formatDate(data.record.data.endDate)
            }
            </div>`
        },
        {
          text: "Medication",
          flex: 1,
          type: "template",
          editor: false,
          sortable: false,
          template: data => {
            return `
            <div class="text-wrap">
              ${data.record.data.medication.label}, ${data.record.data.dosage} ${data.record.data.measurementUnit.label} by ${data.record.data.route.label}
            </div>`;
          }
        },
        {
          text: "Times",
          field: "times",
          type: "template",
          flex: 1,
          editor: false,
          sortable: false,
          template: ({ record }) =>
            `<div class="text-wrap">
              ${Object.values(record.data.times)
                .map(time => this.formatTimeOnly(time, "HH:mm"))
                .join(", ")}
            </div>`
        },
        {
          text: "Frequency",
          type: "template",
          flex: 1,
          editor: false,
          sortable: false,
          template: ({ record }) => {
            let innerText;
            switch (record.data.frequencyId) {
              case "everyDay":
                innerText = "Every Day";
                break;
              case "daysInterval":
                innerText = daysIntervals[record.data.daysIntervalId].label;
                break;
              case "daysOfWeek":
                innerText = [
                  ...(record.data.weekDays.includes("0") ? ["Sunday"] : []),
                  ...(record.data.weekDays.includes("1") ? ["Monday"] : []),
                  ...(record.data.weekDays.includes("2") ? ["Tuesday"] : []),
                  ...(record.data.weekDays.includes("3") ? ["Wednesday"] : []),
                  ...(record.data.weekDays.includes("4") ? ["Thursday"] : []),
                  ...(record.data.weekDays.includes("5") ? ["Friday"] : []),
                  ...(record.data.weekDays.includes("6") ? ["Saturday"] : [])
                ].join(", ");
                break;
              default:
                innerText = "";
                break;
            }
            return `<div class="text-wrap">${innerText}</div>`;
          }
        },
        {
          text: "Instructions",
          field: "instructions",
          type: "template",
          flex: 1,
          template: ({ record }) =>
            `<div class="text-wrap">${
              record.instructions ? record.instructions : ""
            }</div>`,
          editor: false,
          sortable: false
        },
        {
          text: "Protocol",
          field: "predefinedProtocol.label",
          flex: 1,
          editor: false,
          sortable: false
        },
        {
          text: "",
          type: "action",
          field: "action",
          cellCls: "pointer-cell",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "More",
              renderer: ({ record }) => {
                return this.createDropdownButton({
                  items: [
                    {
                      content: "Edit",
                      callback: () => {
                        this.formScheduled = {
                          ...record.data
                        };
                        this.clearErrors();
                        this.$refs["form-scheduled-modal"].show();
                      }
                    },
                    {
                      content: "Delete",
                      callback: () => {
                        this.$dialog
                          .alert(
                            {
                              title:
                                "Are you sure you want to delete this record?"
                            },
                            {
                              view: "confirm-delete"
                            }
                          )
                          .then(async () => {
                            this.form.scheduledMedications.splice(
                              record.data.medicationIndex,
                              1
                            );
                          });
                      }
                    }
                  ]
                });
              }
            }
          ]
        }
      ];
    },
    prnMedicationItems() {
      return this.form.prnMedications.map((item, itemIndex) => {
        let rowHeight = 50;
        const singleLineHeight = 25;
        const charsMedicationPerLine = 40;
        const charsInstructionsPerLine = 50;
        const maxFactor = Math.max(
          Math.ceil(
            `${item.medication.label}, ${item.dosageAmount} ${item.measurementUnit.label} by ${item.route.label}`
              .length / charsMedicationPerLine
          ),
          Math.ceil(item.instructions.length / charsInstructionsPerLine)
        );
        if (maxFactor > 1) rowHeight += singleLineHeight * (maxFactor - 1);
        return {
          medicationIndex: itemIndex,
          ...item,
          rowHeight: rowHeight,
          inputRows: maxFactor
        };
      });
    },
    prnColumns() {
      return [
        {
          text: "Date Range",
          flex: 1,
          type: "template",
          editor: false,
          sortable: false,
          template: data => {
            const displayStartDate =
              data.record.data.startDate !== null &&
              data.record.data.startDate !== undefined;
            const displayEndDate =
              data.record.data.endDate !== null &&
              data.record.data.endDate !== undefined;
            return `
            <div class="text-wrap">
              ${
                displayStartDate
                  ? this.formatDate(data.record.data.startDate)
                  : "Not selected"
              }
              - 
              ${
                displayEndDate
                  ? this.formatDate(data.record.data.endDate)
                  : "Not selected"
              }
            </div>`;
          }
        },
        {
          text: "Medication",
          flex: 1,
          type: "template",
          editor: false,
          sortable: false,
          template: data => {
            return `
            <div class="text-wrap">
              ${data.record.data.medication.label}, ${data.record.data.dosage} ${data.record.data.measurementUnit.label} by ${data.record.data.route.label}
            </div>`;
          }
        },
        {
          text: "Maximum Dosage",
          field: "maximumDosagePerDay",
          flex: 1,
          editor: false,
          sortable: false
        },
        {
          text: "Instructions",
          field: "instructions",
          type: "template",
          flex: 1,
          template: ({ record }) =>
            `<div class="text-wrap">${
              record.instructions ? record.instructions : ""
            }</div>`,
          editor: false,
          sortable: false
        },
        {
          text: "Protocol",
          field: "predefinedProtocol.label",
          flex: 1,
          editor: false,
          sortable: false
        },
        {
          text: "",
          type: "action",
          field: "action",
          cellCls: "pointer-cell",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "More",
              renderer: ({ record }) => {
                return this.createDropdownButton({
                  items: [
                    {
                      content: "Edit",
                      callback: () => {
                        this.formPrn = {
                          ...record.data
                        };
                        this.clearErrors();
                        this.$refs["form-prn-modal"].show();
                      }
                    },
                    {
                      content: "Delete",
                      callback: () => {
                        this.$dialog
                          .alert(
                            {
                              title:
                                "Are you sure you want to delete this record?"
                            },
                            {
                              view: "confirm-delete"
                            }
                          )
                          .then(async () => {
                            this.form.prnMedications.splice(
                              record.data.medicationIndex,
                              1
                            );
                          });
                      }
                    }
                  ]
                });
              }
            }
          ]
        }
      ];
    },
    discontinueColumns() {
      return [
        {
          text: "Date Range",
          flex: 1,
          type: "template",
          editor: false,
          sortable: false,
          template: ({ record }) => {
            return `
            <div class="text-wrap">
              ${
                record.data.startDate
                  ? this.formatDate(record.data.startDate)
                  : "Not selected"
              }
              - 
              ${
                record.data.untilDischarge
                  ? "Until Discharge"
                  : record.data.endDate
                  ? this.formatDate(record.data.endDate)
                  : "Not selected"
              }
            </div>`;
          }
        },
        {
          text: "Medication",
          flex: 1,
          type: "template",
          editor: false,
          sortable: false,
          template: data => {
            return `
            <div class="text-wrap">
              ${data.record.data.medication.label}, ${data.record.data.dosage} ${data.record.data.measurementUnit.label} by ${data.record.data.route.label}
            </div>`;
          }
        },
        {
          text: "Times",
          field: "times",
          type: "template",
          flex: 1,
          editor: false,
          sortable: false,
          template: ({ record }) => {
            if (record.data.type == "prn") {
              return "";
            }

            return `<div class="text-wrap">
              ${Object.values(record.data.times)
                .map(time => this.formatTimeOnly(time, "HH:mm"))
                .join(", ")}
            </div>`;
          }
        },
        {
          text: "Frequency",
          type: "template",
          flex: 1,
          editor: false,
          sortable: false,
          template: ({ record }) => {
            if (record.data.type == "prn") {
              return "";
            }

            let innerText;
            switch (record.data.frequencyId) {
              case "everyDay":
                innerText = "Every Day";
                break;
              case "daysInterval":
                innerText = daysIntervals[record.data.daysIntervalId].label;
                break;
              case "daysOfWeek":
                innerText = [
                  ...(record.data.weekDays.includes("0") ? ["Sunday"] : []),
                  ...(record.data.weekDays.includes("1") ? ["Monday"] : []),
                  ...(record.data.weekDays.includes("2") ? ["Tuesday"] : []),
                  ...(record.data.weekDays.includes("3") ? ["Wednesday"] : []),
                  ...(record.data.weekDays.includes("4") ? ["Thursday"] : []),
                  ...(record.data.weekDays.includes("5") ? ["Friday"] : []),
                  ...(record.data.weekDays.includes("6") ? ["Saturday"] : [])
                ].join(", ");
                break;
              default:
                innerText = "";
                break;
            }
            return `<div class="text-wrap">${innerText}</div>`;
          }
        },
        {
          text: "Instructions",
          field: "instructions",
          flex: 1,
          editor: false,
          sortable: false
        },
        {
          text: "Discontinue",
          type: "template",
          align: "center",
          width: 120,
          editor: false,
          sortable: false,
          template: ({ record }) => {
            return this.createToggleButton({
              checked: this.form.discontinuedMedications.some(
                u => u.id == record.data.id
              ),
              callback: checked => {
                if (checked) {
                  this.form.discontinuedMedications.push({
                    id: record.data.id,
                    type: record.data.type
                  });
                } else {
                  const index = this.form.discontinuedMedications.findIndex(
                    u => u.id == record.data.id
                  );
                  this.form.discontinuedMedications.splice(index, 1);
                }
              }
            });
          }
        }
      ];
    },
    frequencyOptions() {
      return Object.values(frequencies).map(f => ({
        value: f.key,
        label: f.label
      }));
    },
    scheduledFrequency: {
      get() {
        return this.formScheduled.frequencyId
          ? this.frequencyOptions.find(
              frequency => frequency.value == this.formScheduled.frequencyId
            )
          : null;
      },
      set(newValue) {
        this.formScheduled.frequencyId = newValue ? newValue.value : null;
      }
    },
    daysIntervalOptions() {
      return Object.values(daysIntervals).map(d => ({
        value: d.key,
        label: d.label
      }));
    },
    daysInterval: {
      get() {
        return this.formScheduled.daysIntervalId
          ? this.daysIntervalOptions.find(
              daysInterval =>
                daysInterval.value == this.formScheduled.daysIntervalId
            )
          : null;
      },
      set(newValue) {
        this.formScheduled.daysIntervalId = newValue ? newValue.value : null;
      }
    },
    timesPerDayOptions() {
      return Object.values(timesPerDayOptions).map(t => ({
        value: t.key,
        label: t.label
      }));
    },
    timesPerDay: {
      get() {
        return this.formScheduled.timesPerDayId
          ? this.timesPerDayOptions.find(
              timesPerDay =>
                timesPerDay.value == this.formScheduled.timesPerDayId
            )
          : null;
      },
      set(newValue) {
        this.formScheduled.timesPerDayId = newValue ? newValue.value : null;
        this.initializeTimes();
      }
    },
    timeOptions() {
      return timeOptions.map(t => ({
        value: t,
        text: this.formatTimeOnly(t, "HH:mm")
      }));
    }
  },
  watch: {
    "form.predefinedProtocols": function(newValue, oldValue) {
      if (!this.mounted) {
        return;
      }

      const removed = oldValue.filter(p => !newValue.includes(p)).shift();
      if (removed) {
        for (let i = this.form.scheduledMedications.length - 1; i >= 0; i--) {
          const medication = this.form.scheduledMedications[i];
          if (medication.predefinedProtocol?.value == removed.value) {
            this.form.scheduledMedications.splice(i, 1);
          }
        }
        for (let i = this.form.prnMedications.length - 1; i >= 0; i--) {
          const medication = this.form.prnMedications[i];
          if (medication.predefinedProtocol?.value == removed.value) {
            this.form.prnMedications.splice(i, 1);
          }
        }
      }

      const added = newValue.filter(p => !oldValue.includes(p)).shift();
      if (added) {
        this.form.scheduledMedications = this.form.scheduledMedications.concat([
          ...added.data.medications
            .map(m => ({ ...m, id: null }))
            .map(this.mapProtocolScheduledMedication)
        ]);
        this.form.prnMedications = this.form.prnMedications.concat([
          ...added.data.prnMedications
            .map(m => ({ ...m, id: null }))
            .map(this.mapProtocolPrnMedication)
        ]);
      }
    },
    scheduledMedications: function() {
      this.calculateMedicationsInsideRange();
    },
    prnMedications: function() {
      this.calculateMedicationsInsideRange();
    },
    effectiveDateTime: function() {
      this.calculateMedicationsInsideRange();
      this.recalculatePredefinedProtocols();
    }
  }
};
</script>
<style scoped>
.times-container {
  height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
